import { useQueryClient } from "@tanstack/react-query";
import { Sidebar, BaseButton, Input } from "@velocity-ehs/component-library";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ContractingCompanyActions,
  useContractingCompanyMutations,
} from "../../../hooks/useContractingCompaniesMutation";
import classes from "./ContractingCompanySidebar.module.scss";
import { ContractingCompany } from "../Persons/Person";

interface ContractingCompanySidebarProps {
  isVisible: boolean;
  closeSlider: () => void;
  //table function
  setSelectedContractingCompany?: Dispatch<
    SetStateAction<{ id: string; name: string } | null>
  >;
  //person form function
  updateSelectedContractingCompany?: (
    createdCompany: ContractingCompany
  ) => void;
  selectedContractingCompany?: { id: string; name: string } | null;
}

const ContractingCompanySidebar = ({
  closeSlider,
  isVisible,
  selectedContractingCompany,
  setSelectedContractingCompany,
  updateSelectedContractingCompany,
}: ContractingCompanySidebarProps) => {
  const { t } = useTranslation();

  const { mutations } = useContractingCompanyMutations();

  const queryClient = useQueryClient();
  const invalidateContractingCompaniesQuery = () =>
    queryClient.invalidateQueries({ queryKey: ["contracting-companies-v2"] });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ name: string }>({
    mode: "onBlur",
    defaultValues: { name: "" },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (selectedContractingCompany) {
      return reset({ name: selectedContractingCompany.name });
    }

    reset({ name: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContractingCompany]);

  const addContractingCompany = async (formValues: { name: string }) => {
    if (selectedContractingCompany) {
      await mutations.mutate(ContractingCompanyActions.UPDATE, {
        ...formValues,
        id: selectedContractingCompany.id,
      });

      invalidateContractingCompaniesQuery();

      return closeSlider();
    }

    const response = await mutations.mutate(
      ContractingCompanyActions.CREATE,
      formValues
    );

    setSelectedContractingCompany?.(null);

    updateSelectedContractingCompany?.(
      response?.data?.createContractingCompany as ContractingCompany
    );

    invalidateContractingCompaniesQuery();

    return closeSlider();
  };

  return (
    <Sidebar
      header={
        selectedContractingCompany
          ? t("contracting_companies_edit_text")
          : t("contracting_companies_add_text")
      }
      visible={isVisible}
      onHide={closeSlider}
      footer={
        <div className={classes.sidebarFooter}>
          <BaseButton
            form="addContractingCompany"
            type="submit"
            loading={mutations.isLoading}
            label={t("universal_Save")}
          />
          <BaseButton
            label={t("universal_Cancel")}
            className="p-button-text"
            onClick={closeSlider}
          />
        </div>
      }
    >
      <form
        id="addContractingCompany"
        onSubmit={handleSubmit(addContractingCompany)}
      >
        <Input
          control={control}
          name="name"
          label={t("universal_Name")}
          rules={{
            required: t("customer_Form_Name_Required"),
            maxLength: {
              value: 100,
              message: t("max_length_100_char_form_validation"),
            },
          }}
          errorMessage={errors ? errors.name?.message : undefined}
        />
      </form>
    </Sidebar>
  );
};

export { ContractingCompanySidebar };
